import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import Notification from '../core/Notification';
import { globalStyles as styles } from '../core/style/gstheme';
import { makeCancelable } from '../core/utils/utils';
import Warning from '../core/Warning';
import GS from '../grafsync/grafsync';
import CheckingStatus from './CheckingStatus';

class QualityCheckLoader extends React.Component {

    constructor(props) {
        super(props);
        let initialViewStates = {};
        props.views.forEach(fv => initialViewStates[fv] = undefined);
        const cacheTime = window.location.href.includes("localhost") ? 60000 : 600000;
        const intialState = Object.assign({
            viewCount: 0,
            cacheTime,
            stopChecks: false,
            isLoading: true,
            isErrored: false,
            isFailed: false
        }, initialViewStates);
        this.state = intialState;
        this.cancellable = null;
        this.qRef = React.createRef();
    }

    componentDidMount() {
        this.loadViews();
    }

    async loadViews(refreshIfStale = true) {
        // We run linearly to reduce strain on the API
        const { views, disabledFeatures } = this.props;
        const currentTime = Date.now();
        const id = this.props.id;
        for (let i = 0; i < views.length; i++) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            if (this.state.stopChecks) break;
            const view = views[i];
            if (disabledFeatures && disabledFeatures.includes("quality-" + view)) {
                this.setState((prevState) => ({
                    viewCount: prevState.viewCount + 1
                }));
                continue;
            }
            const viewID = view + "-" + this.props.graph + "-" + id;
            const cachedData = GS.retrieveCache(viewID);
            if ((!refreshIfStale && cachedData) || (cachedData && cachedData.time > currentTime - this.state.cacheTime)) {
                this.setState((prevState) => ({
                    [view]: cachedData.data || undefined,
                    [view + "-time"]: cachedData.time,
                    viewCount: prevState.viewCount + 1
                }));
            }
            else {
                GS.clearCache(viewID);
                this.setState({ [view]: undefined });
                // We reset the click idler - we don't want log out kicking in during checking
                if (this.qRef && this.qRef.current) {
                    this.qRef.current.click();
                }
                await this.loadData(view, i)
                    .promise
                    .then((componentData) => {
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        const viewTime = Date.now();
                        // Status indicates an error
                        if (componentData.status) {
                            GS.cache(viewID, []);
                            this.setState({ [view]: [], [view + "-time"]: viewTime, [view + "-error"]: [componentData.status] });
                        }
                        else if (Object.keys(componentData).length > 0) {
                            const data = componentData.graph ? [...componentData.graph] : [componentData];
                            GS.cache(viewID, data);
                            this.setState({ [view]: data, [view + "-time"]: viewTime, [view + "-error"]: [] });
                        }
                        else {
                            GS.cache(viewID, []);
                            this.setState({ [view]: [], [view + "-time"]: viewTime, [view + "-error"]: [] });
                        }
                        this.setState((prevState) => ({
                            viewCount: prevState.viewCount + 1
                        }));
                    })
                    .catch((e) => {
                        if (this.cancellable && !this.cancellable.isCancelled()) {
                            this.setState({
                                isErrored: true,
                                isLoading: false,
                                isFailed: true,
                                message: "Could not load all of the data for the quality view."
                            });
                        }
                        i = views.length;
                    });
            }
        }
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isLoading: false });
    }

    onExitWarning = () => this.setState({ isErrored: false });

    onStop = () => this.setState({ stopChecks: true });

    loadData(view) {
        this.cancellable = makeCancelable(GS.export(view, this.props.id, this.props.graph, "application/ld+json", null, [], [500]));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    onRefresh = (view) => {
        this.setState({
            viewCount: 0,
            stopChecks: false,
            isLoading: true
        }, () => {
            GS.clearCache(view + "-" + this.props.graph + "-" + this.props.id);
            this.loadViews(false);
        });
    };

    render() {

        const { classes, views, displayResults, disabledFeatures } = this.props;
        const { isErrored, message, viewCount, isFailed, isLoading } = this.state;

        if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;
        if (isFailed) return null;

        let viewItems = views.length;
        // If all checks are good then there will no state with errors for any of the views
        const checksSuccessful = !views.some(v => (Array.isArray(this.state[v]) && this.state[v].length > 0) || (this.state[v + "-error"] && this.state[v + "-error"].length > 0));
        let data = {};
        let errorsCount = 0;
        views.forEach(v => {
            data[v] = this.state[v];
            data[v + "-time"] = this.state[v + "-time"];
            data[v + "-error"] = this.state[v + "-error"];
            if (this.state[v + "-error"] && this.state[v + "-error"].length > 0) errorsCount++;
        });
        const disabledViews = disabledFeatures && views.some(v => disabledFeatures.includes("quality-" + v));

        return (
            <div style={{ margin: '6px' }}>
                <CheckingStatus
                    onStop={this.onStop}
                    progressCount={viewCount}
                    checksInconclusive={errorsCount > 0}
                    checksSuccessful={checksSuccessful}
                    itemCount={viewItems} />
                <Typography className={classes.mainHeading} style={{ marginTop: '20px' }}>Quality Check Results</Typography>
                {errorsCount > 0 &&
                    <div style={{ marginTop: 6, marginBottom: 6 }}>
                        <Notification dataTest="errors-happened" text="Some checks failed to run." />
                    </div>
                }
                {disabledViews > 0 &&
                    <div style={{ marginTop: 6, marginBottom: 6 }}>
                        <Notification dataTest="disabled-checks" text="Some checks are disabled. This applies to all users." />
                    </div>
                }
                <Typography paragraph variant="body2" color="textPrimary" style={{ marginBottom: 36 }}>Results will be stored for {this.state.cacheTime / 60000} minutes. If you make fixes within that amount of time you may want to refresh that check to see the latest results.</Typography>
                <div>
                    {displayResults(data, isLoading, this.onRefresh)}
                </div>

            </div >
        );
    }
}

QualityCheckLoader.propTypes = {
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    disabledFeatures: PropTypes.array,
    classes: PropTypes.object,
    displayResults: PropTypes.func.isRequired
};

export default withStyles(styles)(QualityCheckLoader);